import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { type IngestedEventSample } from '@vy/types';
import { isNotEmptyObject, isNullOrUndefined, isObject, vySerialize } from '@vy/utils';
import { SuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import { formatDate } from '../../../../utils/date';
import { AffectedMeteredEvents, MatchedCustomers } from './data-event-drawer-components';

type EventDataDrawerProps = {
  dataDrawerOpen: boolean;
  closeDataDrawer: () => void;
  event?: IngestedEventSample;
  startTime?: Date;
  endTime?: Date;
};

export const EventDataDrawer: React.FC<EventDataDrawerProps> = ({
  dataDrawerOpen,
  closeDataDrawer,
  event,
  startTime,
  endTime,
}) => {
  if (isNullOrUndefined(event)) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      slotProps={{
        backdrop: {
          invisible: true,
        },

        paper: {
          className: 'py-10 px-7 gap-y-7',
          sx: {
            width: '25vw',
          },
        }
      }}
      open={dataDrawerOpen}
      onClose={closeDataDrawer}>
      <IconButton
        onClick={closeDataDrawer}
        style={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <VyIcon icon="CloseBig" />
      </IconButton>
      <Box>
        <Typography variant="h6">{event.name}</Typography>
        <Typography variant="subtitle2">{formatDate(event.timestamp)}</Typography>
      </Box>
      <Box>
        <Typography variant="subtitle1">Event Data</Typography>
        {isNotEmptyObject(event.data) && (
          <Box
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              padding: 1,
              borderRadius: 1,
              overflowX: 'auto',
            }}
          >
            <Typography component="code">{isObject(event.data) ? formatJson(event.data) : ''}</Typography>
          </Box>
        )}
        <Typography
          sx={{
            borderBottom: '1px solid #E0E0E0',
            marginTop: '10px',
          }}
        ></Typography>
      </Box>
      <Box
        sx={{
          minHeight: '150px',
          margin: '10px 0px',
        }}
      >
        <Typography variant="subtitle1">Matched Customer:</Typography>
        <SuspenseBoundary>
          <MatchedCustomers
            customerAlias={event.customerAlias}
            customerName={event.customerName}
            customerMatchedAt={event.matchedAt}
          />
        </SuspenseBoundary>
        <Typography
          sx={{
            borderTop: '1px solid #E0E0E0',
          }}
        ></Typography>
      </Box>
      <Box
        sx={{
          minHeight: '150px',
          margin: '10px 0px',
        }}
      >
        <Typography variant="subtitle1">Affected Meters:</Typography>
        <SuspenseBoundary>
          <AffectedMeteredEvents eventRef={event.ref} startTime={startTime} endTime={endTime} />
        </SuspenseBoundary>
        <Typography
          sx={{
            borderTop: '1px solid #E0E0E0',
          }}
        ></Typography>
      </Box>
    </Drawer>
  );
};

function formatJson(json: Record<string, unknown>) {
  const NEW_LINE = '\n';
  const TAB = '\t';

  const entries = Object.entries(json)
    .map(([key, value]) => `${TAB + key}: ${vySerialize(value)},`)
    .join(NEW_LINE);

  return `{${NEW_LINE + entries + NEW_LINE}}`;
}
